import { useReducer, useCallback } from 'react';
import type { BuildState } from '@/types';
import { SHAFTS } from '@/data/shafts';
import { POINTS } from '@/data/points';
import { resolveLaunchFps } from '@/lib/calculations';

/** Re-derive the effective launch velocity after any build change. */
function withFps(state: BuildState): BuildState {
  return { ...state, fps: resolveLaunchFps(state) };
}

// ============================================================
// Initial state
// ============================================================

export const INITIAL_BUILD: BuildState = {
  name: 'Custom Build #1',
  shaftIdx: 0,
  spineKey: 300,
  length: 29.0,
  pointPreset: 2,
  pointWeight: 100,
  insertIdx: 0,
  collarIdx: -1,  // -1 = no collar selected (HIT insert alone is the default)
  nockIdx: 0,
  vaneIdx: 0,
  vaneCount: 3,
  helicalDeg: 2,  // PCA hunting baseline (Blazer 3-fletch 2° helical)
  wrap: false,
  wrapLen: 7,
  vaneBackPosition: 1.0,  // inches from back of shaft to back of fletch (typical hunting)
  fps: 285,
  bow: {
    type: 'compound',
    drawWeight: 70,
    drawLength: 29.0,
    iboSpeed: 335,
    letOff: 80,
  },
  ctiPosition: 'none',
  ctiFrontLength: 6.0,
  ctiFrontWeight: 30,
  ctiBackLength: 6.0,
  ctiBackWeight: 30,
};

// ============================================================
// Actions
// ============================================================

type BuildAction =
  /** Generic single-field set. Use `setField` helper for type safety. */
  | { type: 'SET_FIELD'; field: keyof BuildState; value: unknown }
  /** Setting shaft auto-corrects spine if the previous spine doesn't exist on the new shaft. */
  | { type: 'SET_SHAFT'; idx: number }
  /** Setting point preset auto-syncs the weight slider. */
  | { type: 'SET_POINT_PRESET'; idx: number }
  /** Replace entire build (load saved / import). */
  | { type: 'LOAD'; payload: BuildState }
  | { type: 'RESET' };

function reducer(state: BuildState, action: BuildAction): BuildState {
  switch (action.type) {
    case 'SET_FIELD':
      return withFps({ ...state, [action.field]: action.value } as BuildState);

    case 'SET_SHAFT': {
      const next = SHAFTS[action.idx];
      if (!next) return state;
      const validSpines = Object.keys(next.spines).map(Number);
      const spineKey = validSpines.includes(state.spineKey)
        ? state.spineKey
        : (validSpines[Math.floor(validSpines.length / 2)] ?? validSpines[0]);
      return withFps({ ...state, shaftIdx: action.idx, spineKey });
    }

    case 'SET_POINT_PRESET': {
      const preset = POINTS[action.idx];
      if (!preset) return state;
      return withFps({ ...state, pointPreset: action.idx, pointWeight: preset.weight });
    }

    case 'LOAD':
      // Preserve a loaded build's stored fps (legacy builds have no measuredFps);
      // only re-derive when the build carries an explicit measured reading.
      return action.payload.measuredFps ? withFps({ ...action.payload }) : { ...action.payload };

    case 'RESET':
      return withFps({ ...INITIAL_BUILD });

    default:
      return state;
  }
}

// ============================================================
// Hook
// ============================================================

export interface UseBuildState {
  state: BuildState;
  setField: <K extends keyof BuildState>(field: K, value: BuildState[K]) => void;
  setShaft: (idx: number) => void;
  setPointPreset: (idx: number) => void;
  load: (state: BuildState) => void;
  reset: () => void;
}

export function useBuildState(initial: BuildState = INITIAL_BUILD): UseBuildState {
  const [state, dispatch] = useReducer(reducer, initial);

  const setField = useCallback(
    <K extends keyof BuildState>(field: K, value: BuildState[K]) => {
      dispatch({ type: 'SET_FIELD', field, value });
    },
    [],
  );

  const setShaft = useCallback((idx: number) => dispatch({ type: 'SET_SHAFT', idx }), []);
  const setPointPreset = useCallback(
    (idx: number) => dispatch({ type: 'SET_POINT_PRESET', idx }),
    [],
  );
  const load = useCallback((payload: BuildState) => dispatch({ type: 'LOAD', payload }), []);
  const reset = useCallback(() => dispatch({ type: 'RESET' }), []);

  return { state, setField, setShaft, setPointPreset, load, reset };
}
